import { motion, useReducedMotion } from "motion/react";
import { resort } from "@/data/resort";
import { HeroPhoto } from "../OptimizedImage";
import { ResortLink } from "../resort-button";

export function ContactHero() {
  const reduced = useReducedMotion();
  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <section
      id="top"
      aria-labelledby="contact-hero-heading"
      className="relative min-h-[54svh] overflow-hidden bg-forest-deep md:min-h-[56svh]"
    >
      <motion.div
        className="absolute inset-0"
        initial={reduced ? false : { scale: 1.04 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2, ease }}
      >
        <HeroPhoto alt="Oak trees and RV sites at Auburn RV Resort in the Sierra Foothills" />
        <div className="overlay-cinematic absolute inset-0" />
      </motion.div>

      <div className="shell relative flex min-h-[54svh] flex-col justify-end pb-10 pt-18 md:min-h-[56svh] md:pb-14 md:pt-20">
        <motion.p
          className="eyebrow text-primary-foreground/80"
          initial={reduced ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease }}
        >
          Get in Touch
        </motion.p>

        <h1
          id="contact-hero-heading"
          className="display-xl mt-5 max-w-[16ch] text-primary-foreground"
        >
          <span className="block overflow-hidden">
            <motion.span
              className="display-xl block"
              initial={reduced ? false : { y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.28, ease }}
            >
              Have a Question?
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              className="display-xl block"
              initial={reduced ? false : { y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.38, ease }}
            >
              We&apos;re Here to Help.
            </motion.span>
          </span>
        </h1>

        <motion.p
          className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/80 md:text-lg"
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease }}
        >
          Whether you have a question about your stay, our RV sites, resort amenities or the Auburn
          area, our team is ready to help.
        </motion.p>

        <motion.div
          className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row"
          initial={reduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease }}
        >
          <ResortLink variant="gold" href={resort.phoneHref} className="w-full sm:w-auto">
            Call {resort.phone}
          </ResortLink>
          <ResortLink variant="onDark" href="#contact-form" className="w-full sm:w-auto">
            Send Us a Message
          </ResortLink>
        </motion.div>

        <motion.p
          className="mt-6 text-sm text-primary-foreground/75"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1, ease }}
        >
          Looking to stay with us?{" "}
          <a href="/reservations/" className="eyebrow text-accent underline-offset-4 hover:underline">
            Check Availability →
          </a>
        </motion.p>
      </div>
    </section>
  );
}
